import { motion } from 'framer-motion';
import { CSSProperties, ReactNode } from 'react';

interface ButtonProps {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  variant?: 'primary' | 'secondary' | 'outline';
  disabled?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
}

export default function Button({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  disabled = false,
  fullWidth = false,
  loading = false
}: ButtonProps) {
  const baseStyles: CSSProperties = {
    padding: '0.75rem 2rem',
    fontSize: '1rem',
    fontWeight: 600,
    borderRadius: 'var(--radius-md)',
    border: 'none',
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    width: fullWidth ? '100%' : 'auto',
    opacity: disabled || loading ? 0.6 : 1
  };

  const variantStyles: Record<string, CSSProperties> = {
    primary: {
      background: 'var(--color-primary)',
      color: 'white'
    },
    secondary: {
      background: 'var(--color-secondary)',
      color: 'white'
    },
    outline: {
      background: 'transparent',
      color: 'var(--color-primary)',
      border: '2px solid var(--color-primary)'
    }
  };

  return (
    <motion.button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      style={{ ...baseStyles, ...variantStyles[variant] }}
      whileHover={disabled || loading ? {} : { scale: 1.02, boxShadow: 'var(--shadow-lg)' }}
      whileTap={disabled || loading ? {} : { scale: 0.98 }}
    >
      {loading && (
        <motion.div
          style={{
            width: '16px',
            height: '16px',
            border: '2px solid currentColor',
            borderTopColor: 'transparent',
            borderRadius: '50%'
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
        />
      )}
      {children}
    </motion.button>
  );
}
